import { CircularProgress, Stack } from '@mui/material'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useEngagement, useEngagementActivities } from '../../../../api/apiQueries'
import { Banner, BannerType } from '../../../../components/Banner/Banner'
import { EngagementDetails } from '../EngagementSidePanel/EngagementDetails'
import { SidePanel } from '../EngagementSidePanel/SidePanel'
import { NoEngagementDataSidePanel, SidePanelSkeleton } from '../EngagementSidePanel/SidePanelStates'
import { SpecialistActivities } from '../EngagementSidePanel/SpecialistActivities'
import { ErtPlayer, ErtPlayerRef } from './ErtPlayer'
import { useErtRecordingData } from './useErtRecordingData'
import { useSidePanelOutletContext } from '../../../../hooks/useSidePanelOutletContext'

// QA (engagement) mode. Renders the ErtPlayer body into ErtPage's <Outlet> and feeds the mode-specific
// header + side panel up to the shared <Page> via the slot context.
export const ErtQA: FC = () => {
  const { sid: locationId, engagementId } = useParams()
  const { setSidePanelAddOn } = useSidePanelOutletContext()

  const engagementQuery = useEngagement(engagementId)
  const engagement = engagementQuery.data
  const activitiesQuery = useEngagementActivities(engagementId)
  const activities = activitiesQuery.data

  const { webRecordings, audioRecordings, processedEvents, processedActivities, isRecordingsLoading, error } = useErtRecordingData({
    id: engagementId,
    locationId,
    includeVideo: true,
    activities
  })

  const [currentTimeInSeconds, setCurrentTimeInSeconds] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)

  const ertPlayerRef = useRef<ErtPlayerRef>(null)
  // Activity card clicks should seek and resume playback from that point.
  const handleActivitySeek = useCallback((timeInSeconds: number) => {
    ertPlayerRef.current?.seek(timeInSeconds)
  }, [])

  const isLoading = engagementQuery.isLoading || activitiesQuery.isLoading || isRecordingsLoading

  // This feeds into the parent ErtPage's <Page> slots
  useEffect(() => {
    setSidePanelAddOn(
      isLoading ? (
        <SidePanelSkeleton />
      ) : engagement && processedActivities ? (
        <SidePanel testIdPrefix="engagement-">
          <EngagementDetails engagement={engagement} activities={processedActivities} />
          <SpecialistActivities
            engagement={engagement}
            processedActivities={processedActivities}
            scrubberCurrentTimeInSeconds={currentTimeInSeconds}
            isPlaying={isPlaying}
            onSeek={handleActivitySeek}
          />
        </SidePanel>
      ) : (
        <NoEngagementDataSidePanel />
      )
    )
  }, [setSidePanelAddOn, engagement, processedActivities, isLoading, currentTimeInSeconds, isPlaying, handleActivitySeek])

  return locationId && engagementId && webRecordings && audioRecordings && processedEvents && processedActivities ? (
    <ErtPlayer
      ref={ertPlayerRef}
      locationId={locationId}
      engagementId={engagementId}
      processedActivities={processedActivities}
      onCurrentTimeChange={setCurrentTimeInSeconds}
      onIsPlayingChange={setIsPlaying}
      webRecordings={webRecordings}
      audioRecordings={audioRecordings}
      processedRRWebEvents={processedEvents}
      recordingStartTimestamp={processedEvents ? processedEvents.timeOfFirstEvent : undefined}
      sx={{ alignSelf: 'center', height: '100%', width: '100%', minHeight: 0 }}
    />
  ) : error ? (
    <Stack data-testid="ert-error-banner" sx={{ alignItems: 'center' }}>
      <Banner type={BannerType.Error} title={'Error'} body={error} />
    </Stack>
  ) : (
    <Stack data-testid="ert-loading-spinner" sx={{ justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
      <CircularProgress />
    </Stack>
  )
}
